import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import cx from 'classnames';
import {
  containerQueryProps,
  Link,
  TranslationProvider,
} from '@resy/ui-kitchen';
import ClampLines from '../../../../../../ui-kitchen/src/components/ClampLines/ClampLines';

import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';

import './BlogHero.scss';

const BlogHero = (props) => {
  const {
    author_display_name: authorName,
    clampTitle,
    className,
    containerQuery: { md, lg, xl },
    image,
    name,
    noSubtitle,
    post_date: postDate,
    subtitle,
    tagUrl,
    title,
    url,
  } = props;

  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubTitle = sanitizeString(subtitle);

  const formattedPostDate = useMemo(() => moment(postDate).format('LL'), [postDate]);

  return (
    <div
      className={cx('BlogHero', {
        [className]: typeof className !== 'undefined',
        'BlogHero--md': md,
        'BlogHero--lg': lg,
        'BlogHero--xl': xl,
      })}
      aria-label={`${title} - ${subtitle}`}
    >
      <Link
        className={cx('BlogHero__Image', {
          'BlogHero__Image--md': md,
          'BlogHero__Image--lg': lg,
          'BlogHero__Image--xl': xl,
        })}
        href={url}
        variant="article"
        tabIndex="-1"
      >
        <div
          className={cx({
            'skeleton-animation': !image,
          })}
          style={{
            backgroundImage: `url(${image})`,
          }}
        />
      </Link>

      <div className={cx('BlogHero__Content', {
        'BlogHero__Content--md': md,
        'BlogHero__Content--lg': lg,
        'BlogHero__Content--xl': xl,
      })}
      >
        <Link
          className={cx('BlogHero__Tag header-tag', {
            skeleton: !name,
          })}
          href={tagUrl}
          variant="article"
        >
          <h2>{name}</h2>
        </Link>
        <Link
          className="BlogHero__Content--link"
          href={url}
          variant="article"
          aria-label={title}
        >
          {clampTitle ? (
            <h2
              className={cx('BlogHero__Title', {
                skeleton: !title,
              })}
            >
              <ClampLines
                text={title}
                id={`BlogHero--title-${url}`}
                lines={2}
                buttons={false}
              />
            </h2>
          ) : (
            <h2
              className={cx('BlogHero__Title', {
                skeleton: !title,
              })}
              dangerouslySetInnerHTML={{
                __html: sanitizedTitle,
              }}
            />
          )}
        </Link>
        {!noSubtitle && (
          <p
            className={cx('BlogHero__Subtitle body--sm color--text-secondary', {
              skeleton: !subtitle,
            })}
            dangerouslySetInnerHTML={{
              __html: sanitizedSubTitle,
            }}
            aria-label={subtitle}
          />
        )}
        {authorName
          && (
            <div className="BlogPost__author">
              <TranslationProvider translationKey="by_author" translationValues={{ author: authorName }}>
                <span>
                  By
                  {' '}
                  {authorName}
                </span>
              </TranslationProvider>
              <span>&nbsp; &middot; &nbsp;</span>
              <span>{formattedPostDate}</span>
            </div>
          )}
      </div>
    </div>
  );
};

BlogHero.propTypes = {
  author_display_name: PropTypes.string,
  clampTitle: PropTypes.bool,
  className: PropTypes.string,
  containerQuery: containerQueryProps,
  image: PropTypes.string,
  name: PropTypes.string,
  noSubtitle: PropTypes.bool,
  post_date: PropTypes.string,
  subtitle: PropTypes.string,
  tagUrl: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
};

BlogHero.defaultProps = {
  author_display_name: undefined,
  clampTitle: false,
  className: undefined,
  containerQuery: {},
  image: '',
  name: '',
  noSubtitle: false,
  post_date: undefined,
  subtitle: '',
  tagUrl: '',
  title: '',
  url: '',
};

BlogHero.displayName = 'BlogHero';
export default BlogHero;
